/**
 * WebXR VR mode: an "Enter VR" button that exists only on devices that can run
 * an immersive-vr session (Quest Browser, Vision Pro Safari, desktop Chrome
 * with a PC headset). Desktops and phones without WebXR VR never see it.
 *
 * In VR the headset drives the camera, so the 2D controller is paused and the
 * camera is parented to a rig entity: the rig is where the visitor stands, the
 * head pose is local to it. Moving = moving the rig.
 *
 *  - Teleport: point a controller (or pinch-and-point on Vision Pro) at the
 *    floor, a ring shows where you will land, release the trigger to go there.
 *  - Snap turn: thumbstick left/right turns 30° around the head.
 *
 * Where the scene has a `walk` block, teleports land only on its surfaces
 * (floor outline, levels, stairs), at their real height. Without one, the
 * floor is a horizontal plane one standing eye height below the camera at the
 * moment VR starts, so the visitor's eyes land where the 2D view was.
 */

import * as pc from 'playcanvas';

import type { ViewerApp } from '../app';
import {
    buildWalkSurfaces, raycastWalkSurfaces, seedWalkPoint,
    type WalkConfigLike, type WalkSurface
} from '../walk-geometry';

/** Eye height assumed when placing the floor under the 2D camera. */
const EYE_HEIGHT = 1.6;
/** Teleport reach along the floor, metres. */
const MAX_TELEPORT = 10;
const SNAP_TURN_DEG = 30;
/** Thumbstick deflection that counts as a turn, and the one that re-arms it. */
const STICK_ON = 0.6;
const STICK_OFF = 0.3;
/**
 * Splat budget while in a headset: two eyes at 72-90 fps on a mobile GPU.
 * `?vr.budget=<n>` overrides it for tuning on a real device.
 */
const VR_SPLAT_BUDGET = 1_500_000;

/**
 * Preflight before the graphics device exists: which backends to try so VR
 * can work. WebGPU stays first when the browser can bind it to XR; otherwise
 * a VR-capable device boots on WebGL2. Returns null to keep the default order.
 */
export async function vrDeviceTypes(): Promise<string[] | null> {
    if (!('xr' in navigator)) return null;
    const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 1500));
    try {
        const vr = await Promise.race([
            pc.XrManager.isDeviceSupported(pc.DEVICETYPE_WEBGL2, pc.XRTYPE_VR),
            timeout
        ]);
        if (!vr) return null;
        const vrOnGpu = await Promise.race([
            pc.XrManager.isDeviceSupported(pc.DEVICETYPE_WEBGPU, pc.XRTYPE_VR),
            timeout
        ]);
        return vrOnGpu ? null : [pc.DEVICETYPE_WEBGL2, pc.DEVICETYPE_WEBGPU];
    } catch {
        return null;
    }
}

export interface VrModeOptions {
    viewer: ViewerApp;
    parent: HTMLElement;
    /** The scene's walk block, when it has one: teleport targets come from it. */
    walk?: WalkConfigLike;
    /** Stop the 2D controller (it would fight the headset for the camera). */
    onEnter: () => void;
    /** Hand the camera back to the 2D controller at this pose. */
    onExit: (pos: pc.Vec3, target: pc.Vec3) => void;
    /** Pause / resume adaptive quality, which would otherwise retune the budget. */
    pauseQuality?: () => void;
    resumeQuality?: () => void;
}

export class VrMode {

    private readonly viewer: ViewerApp;

    private readonly xr: pc.XrManager;

    private readonly opts: VrModeOptions;

    private readonly button: HTMLButtonElement;

    private rig: pc.Entity | null = null;

    private reticle: pc.Entity | null = null;

    /** Fallback floor height when the scene has no walk surfaces. */
    private floorY = 0;

    private readonly surfaces: WalkSurface[];

    private savedBudget = 0;

    /** Input sources currently aiming (trigger / pinch held). */
    private readonly aiming = new Set<pc.XrInputSource>();

    /** Per-source snap-turn latch, so one push = one turn. */
    private readonly turnArmed = new Map<pc.XrInputSource, boolean>();

    private readonly offs: Array<() => void> = [];

    constructor(opts: VrModeOptions) {
        this.opts = opts;
        this.viewer = opts.viewer;
        this.xr = opts.viewer.app.xr!;
        this.surfaces = opts.walk ? buildWalkSurfaces(opts.walk) : [];

        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'ollo-btn ollo-vr-btn';
        this.button.textContent = 'Enter VR';
        this.button.hidden = true;
        this.button.addEventListener('click', () => this.enter());
        opts.parent.appendChild(this.button);

        const refresh = () => { this.button.hidden = !this.available(); };
        this.listen(this.xr, `available:${pc.XRTYPE_VR}`, refresh);
        this.listen(this.xr, 'start', () => this.onStart());
        this.listen(this.xr, 'end', () => this.onEnd());
        this.listen(this.xr.input, 'add', (s: pc.XrInputSource) => this.onInputAdd(s));
        this.listen(this.xr.input, 'remove', (s: pc.XrInputSource) => {
            this.aiming.delete(s);
            this.turnArmed.delete(s);
        });
        this.listen(this.viewer.app, 'update', () => this.update());
        refresh();
    }

    private listen(target: pc.EventHandler, name: string, fn: (...args: any[]) => void): void {
        target.on(name, fn);
        this.offs.push(() => target.off(name, fn));
    }

    private available(): boolean {
        // the ported SuperSplat renderer (?sr=1) draws one view only
        return this.xr.supported && this.xr.isAvailable(pc.XRTYPE_VR) && !this.viewer.splatRenderer;
    }

    private enter(): void {
        if (this.xr.active || !this.available()) return;
        const camera = this.viewer.cameraEntity.camera!;
        camera.startXr(pc.XRTYPE_VR, pc.XRSPACE_LOCALFLOOR, {
            callback: (err) => {
                if (err) console.warn('viewer2: could not start VR', err);
            }
        });
    }

    private onStart(): void {
        const cam = this.viewer.cameraEntity;
        this.opts.onEnter();
        this.opts.pauseQuality?.();
        this.viewer.enterXrRendering();

        const gs = this.viewer.app.scene.gsplat;
        this.savedBudget = gs.splatBudget;
        const override = Number(new URL(window.location.href).searchParams.get('vr.budget'));
        gs.splatBudget = Number.isFinite(override) && override > 0 ? override : Math.min(this.savedBudget, VR_SPLAT_BUDGET);

        // stand the visitor where the 2D camera was, facing the same way
        const pos = cam.getPosition().clone();
        const fwd = cam.forward;
        this.floorY = pos.y - EYE_HEIGHT;
        let feet = { x: pos.x, y: this.floorY, z: pos.z };
        if (this.surfaces.length) {
            const p = seedWalkPoint(this.surfaces, pos.x, pos.z, this.floorY);
            feet = { x: p.x, y: p.y, z: p.z };
        }
        const rig = new pc.Entity('vr-rig');
        rig.setPosition(feet.x, feet.y, feet.z);
        rig.setEulerAngles(0, Math.atan2(-fwd.x, -fwd.z) * pc.math.RAD_TO_DEG, 0);
        this.viewer.app.root.addChild(rig);
        cam.reparent(rig);
        cam.setLocalPosition(0, EYE_HEIGHT, 0);
        cam.setLocalEulerAngles(0, 0, 0);
        this.rig = rig;

        this.reticle = this.makeReticle();
        this.button.hidden = true;
    }

    private onEnd(): void {
        const cam = this.viewer.cameraEntity;
        const pos = cam.getPosition().clone();
        const fwd = cam.forward.clone();
        // level the view: a head tilted down would hand orbit a steep pitch
        fwd.y = 0;
        if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
        fwd.normalize();
        const target = new pc.Vec3().add2(pos, fwd.mulScalar(5));

        cam.reparent(this.viewer.app.root);
        cam.setPosition(pos);
        cam.lookAt(target);
        this.rig?.destroy();
        this.rig = null;
        this.reticle?.destroy();
        this.reticle = null;
        this.aiming.clear();
        this.turnArmed.clear();

        this.viewer.app.scene.gsplat.splatBudget = this.savedBudget;
        this.viewer.exitXrRendering();
        this.opts.resumeQuality?.();
        this.opts.onExit(pos, target);
        this.button.hidden = !this.available();
    }

    private onInputAdd(source: pc.XrInputSource): void {
        this.turnArmed.set(source, true);
        source.on('selectstart', () => this.aiming.add(source));
        source.on('selectend', () => {
            if (!this.aiming.delete(source)) return;
            const hit = this.floorHit(source);
            if (hit) this.teleport(hit);
        });
    }

    /** Where the source's ray meets the floor, within reach; null if it doesn't. */
    private floorHit(source: pc.XrInputSource): pc.Vec3 | null {
        const origin = source.getOrigin();
        const dir = source.getDirection();
        let hit: pc.Vec3;
        if (this.surfaces.length) {
            const r = raycastWalkSurfaces([origin.x, origin.y, origin.z], [dir.x, dir.y, dir.z], this.surfaces);
            if (!r) return null;
            hit = new pc.Vec3(r.point[0], r.point[1], r.point[2]);
        } else {
            if (dir.y > -0.05) return null;
            const t = (this.floorY - origin.y) / dir.y;
            if (t <= 0) return null;
            hit = new pc.Vec3(origin.x + dir.x * t, this.floorY, origin.z + dir.z * t);
        }
        const head = this.viewer.cameraEntity.getPosition();
        const dx = hit.x - head.x;
        const dz = hit.z - head.z;
        return dx * dx + dz * dz <= MAX_TELEPORT * MAX_TELEPORT ? hit : null;
    }

    /** Move the rig so the visitor's feet (the head projected down) land on `hit`. */
    private teleport(hit: pc.Vec3): void {
        if (!this.rig) return;
        const head = this.viewer.cameraEntity.getPosition();
        const rigPos = this.rig.getPosition();
        this.rig.setPosition(rigPos.x + hit.x - head.x, hit.y, rigPos.z + hit.z - head.z);
    }

    /** Turn the rig about the vertical line through the head. */
    private snapTurn(deg: number): void {
        if (!this.rig) return;
        const head = this.viewer.cameraEntity.getPosition().clone();
        const q = new pc.Quat().setFromEulerAngles(0, deg, 0);
        const offset = new pc.Vec3().sub2(this.rig.getPosition(), head);
        q.transformVector(offset, offset);
        this.rig.setPosition(head.x + offset.x, this.rig.getPosition().y, head.z + offset.z);
        this.rig.rotate(0, deg, 0);
    }

    private update(): void {
        if (!this.xr.active || !this.rig) return;

        let aim: pc.Vec3 | null = null;
        for (const source of this.xr.input.inputSources) {
            // the reticle follows whatever is aiming; a controller also previews while idle
            if (!aim && (this.aiming.has(source) || source.targetRayMode === pc.XRTARGETRAY_POINTER)) {
                aim = this.floorHit(source);
            }

            const pad = source.gamepad;
            if (!pad) continue;
            // xr-standard mapping: thumbstick is axes 2/3 (touchpad 0/1 on older controllers)
            const x = pad.axes.length >= 4 ? pad.axes[2] : (pad.axes[0] ?? 0);
            const armed = this.turnArmed.get(source) ?? true;
            if (armed && Math.abs(x) > STICK_ON) {
                this.snapTurn(x > 0 ? -SNAP_TURN_DEG : SNAP_TURN_DEG);
                this.turnArmed.set(source, false);
            } else if (!armed && Math.abs(x) < STICK_OFF) {
                this.turnArmed.set(source, true);
            }
        }

        if (this.reticle) {
            this.reticle.enabled = !!aim;
            if (aim) this.reticle.setPosition(aim.x, aim.y + 0.01, aim.z);
        }
    }

    private makeReticle(): pc.Entity {
        const material = new pc.StandardMaterial();
        material.diffuse = new pc.Color(0, 0, 0);
        material.emissive = new pc.Color(0xc8 / 255, 0x70 / 255, 0x48 / 255);  // brand #c87048
        material.useLighting = false;
        material.update();
        const ring = new pc.Entity('vr-reticle');
        ring.addComponent('render', { type: 'torus', material, castShadows: false });
        ring.setLocalScale(0.5, 0.15, 0.5);
        ring.enabled = false;
        this.viewer.app.root.addChild(ring);
        return ring;
    }

    dispose(): void {
        if (this.xr.active) this.xr.end();
        for (const off of this.offs) off();
        this.offs.length = 0;
        this.button.remove();
    }
}
